/** 三处同源比对屏取数与解析（G3，#4604）。
 *
 * `GET /parity/crosscheck` 只读透出 someo-backtrader `reports/keel-crosscheck.json`：
 * 主指标 `order_sequence_match_rate`（裁决 B5），其余三个比率并排作辅助口径；
 * 差异四类（撮合时点 / 费用 / 滑点 / 映射规则）只认产物字段 `four_class`，为 `null` 时四类标「缺」，
 * 操作台**不自行归类**——产物自带的八类 `categories` 原样列出。
 * 产物不存在时后端 501「只读端点未接数据源」，界面显示「接口未提供」，不显示 0%。
 */

import { apiUrl, expectList, expectRecord, optionalNumber, optionalString, query, requireNumber, requireString } from "../../api/client";
import { ContractError } from "../../api/errors";

export const PRIMARY_METRIC = "order_sequence_match_rate";
export const AUX_METRICS = ["order_multiset_match_rate", "symbol_side_sequence_match_rate", "symbol_side_multiset_match_rate"] as const;

export interface ParityStrategy {
  key: string;
  strategy_id: string | null;
  days: number | null;
  backtrader_orders: number | null;
  keel_orders: number | null;
  comparison_denominator: number | null;
  /** 主指标与三个辅助比率；产物没给时为 null。 */
  rates: Record<string, number | null>;
  max_equity_relative_deviation: number | null;
  max_fill_price_abs_diff: number | null;
  max_commission_abs_diff: number | null;
  categories: Record<string, number> | null;
  four_class: Record<string, number> | null;
  halted: boolean;
  halt_reason: string | null;
  ledger_digest: string | null;
  daily_count: number;
}

export interface ParityPayload {
  path: string;
  file_sha256: string;
  file_mtime_utc: string;
  schema_version: number;
  primary_metric: string;
  four_class_labels: string[];
  source: Record<string, unknown>;
  method: Record<string, string>;
  category_explanations: Record<string, string>;
  strategies: ParityStrategy[];
}

function counts(value: unknown, url: string, what: string): Record<string, number> | null {
  if (value === null || value === undefined) return null;
  const r = expectRecord(value, url, what);
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(r)) {
    if (typeof v !== "number") throw new ContractError(`${what}.${k} 应为数字`, url, r);
    out[k] = v;
  }
  return out;
}

function texts(value: unknown, url: string, what: string): Record<string, string> {
  if (value === null || value === undefined) return {};
  const r = expectRecord(value, url, what);
  return Object.fromEntries(Object.entries(r).map(([k, v]) => [k, typeof v === "string" ? v : JSON.stringify(v)]));
}

export function parseParity(body: unknown): ParityPayload {
  const url = apiUrl("/parity/crosscheck");
  const root = expectRecord(body, url, "交叉核对");
  const labels = root["four_class_labels"];
  if (!Array.isArray(labels) || labels.some((x) => typeof x !== "string")) throw new ContractError("four_class_labels 应为字符串数组", url, root);
  return {
    path: requireString(root, "path", url),
    file_sha256: requireString(root, "file_sha256", url),
    file_mtime_utc: requireString(root, "file_mtime_utc", url),
    schema_version: requireNumber(root, "schema_version", url),
    primary_metric: requireString(root, "primary_metric", url),
    four_class_labels: labels as string[],
    source: root["source"] && typeof root["source"] === "object" ? (root["source"] as Record<string, unknown>) : {},
    method: texts(root["method"], url, "method"),
    category_explanations: texts(root["category_explanations"], url, "category_explanations"),
    strategies: expectList(root, "strategies", url, "strategies").map((s) => ({
      key: requireString(s, "key", url),
      strategy_id: optionalString(s, "strategy_id"),
      days: optionalNumber(s, "days"),
      backtrader_orders: optionalNumber(s, "backtrader_orders"),
      keel_orders: optionalNumber(s, "keel_orders"),
      comparison_denominator: optionalNumber(s, "comparison_denominator"),
      rates: Object.fromEntries([PRIMARY_METRIC, ...AUX_METRICS].map((m) => [m, optionalNumber(s, m)])),
      max_equity_relative_deviation: optionalNumber(s, "max_equity_relative_deviation"),
      max_fill_price_abs_diff: optionalNumber(s, "max_fill_price_abs_diff"),
      max_commission_abs_diff: optionalNumber(s, "max_commission_abs_diff"),
      categories: counts(s["categories"], url, "categories"),
      four_class: counts(s["four_class"], url, "four_class"),
      halted: s["halted"] === true,
      halt_reason: optionalString(s, "halt_reason"),
      ledger_digest: optionalString(s, "ledger_digest"),
      daily_count: optionalNumber(s, "daily_count") ?? 0,
    })),
  };
}

/** 一致率显示：分母为 0 时「无订单可比」，不写 0% 也不写 100%；产物没给时「缺」。 */
export function rateText(rate: number | null, denominator: number | null): string {
  if (denominator === 0) return "无订单可比";
  if (rate === null) return "缺";
  return `${(rate * 100).toFixed(2)}%`;
}

/** 四类归属：只认产物字段；没有就逐类标「缺」，不从八类里推。 */
export function fourClassCells(labels: string[], fourClass: Record<string, number> | null): { label: string; value: string }[] {
  return labels.map((label) => ({ label, value: fourClass === null ? "缺" : label in fourClass ? String(fourClass[label]) : "缺" }));
}

export interface DailyRow {
  day: string;
  backtrader_orders: number | null;
  keel_orders: number | null;
  sequence_matches: number | null;
  multiset_matches: number | null;
  equity_abs_diff: number | null;
}

export function parseDaily(body: unknown): DailyRow[] {
  const url = apiUrl("/parity/crosscheck/daily");
  return expectList(body, "daily", url, "逐日对照").map((r) => ({
    day: requireString(r, "day", url),
    backtrader_orders: optionalNumber(r, "backtrader_orders"),
    keel_orders: optionalNumber(r, "keel_orders"),
    sequence_matches: optionalNumber(r, "sequence_matches"),
    multiset_matches: optionalNumber(r, "multiset_matches"),
    equity_abs_diff: optionalNumber(r, "equity_abs_diff"),
  }));
}

export interface DayOrders {
  backtrader: Record<string, unknown>[];
  keel: Record<string, unknown>[];
}

export function parseDayOrders(body: unknown): DayOrders {
  const url = apiUrl("/parity/crosscheck/daily");
  const day = expectRecord(expectRecord(body, url, "逐日")["day"], url, "day");
  const orders = expectRecord(day["orders"], url, "day.orders");
  return { backtrader: expectList(orders, "backtrader", url, "orders.backtrader"), keel: expectList(orders, "keel", url, "orders.keel") };
}

export const readParity = (): Promise<ParityPayload> => query<unknown>("/parity/crosscheck").then(parseParity);
export const readParityDaily = (strategy: string): Promise<DailyRow[]> => query<unknown>("/parity/crosscheck/daily", { strategy }).then(parseDaily);
export const readParityDay = (strategy: string, day: string): Promise<DayOrders> =>
  query<unknown>("/parity/crosscheck/daily", { strategy, day }).then(parseDayOrders);

/** 三处同源列：运行索引里按模式取最近一次（只并排原值，不判定）。 */
export interface SameSourceRun {
  run_id: string;
  mode: string;
  strategy_id: string | null;
  trade_date: string | null;
  config_digest: string | null;
  snapshot_id: string | null;
  ledger_digest: string | null;
  orders: number | null;
  fills: number | null;
}

export function parseSameSourceRuns(body: unknown): SameSourceRun[] {
  const url = apiUrl("/runs");
  return expectList(body, "runs", url, "运行索引").map((r) => ({
    run_id: requireString(r, "run_id", url),
    mode: requireString(r, "mode", url),
    strategy_id: optionalString(r, "strategy_id"),
    trade_date: optionalString(r, "trade_date"),
    config_digest: optionalString(r, "config_digest"),
    snapshot_id: optionalString(r, "snapshot_id"),
    ledger_digest: optionalString(r, "ledger_digest"),
    orders: optionalNumber(r, "orders"),
    fills: optionalNumber(r, "fills"),
  }));
}

export const readSameSourceRuns = (): Promise<SameSourceRun[]> => query<unknown>("/runs").then(parseSameSourceRuns);

/** 每个模式取最后一条运行（运行索引按时间升序）。 */
export function latestByMode(runs: SameSourceRun[], modes: string[]): Record<string, SameSourceRun | null> {
  return Object.fromEntries(modes.map((m) => [m, [...runs].reverse().find((r) => r.mode === m) ?? null]));
}
